import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Chip, Container, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { listOrders } from '../services/Common/orders.service';
import palette from '../theme/palette';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [customer, setCustomer] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const [rowCount, setRowCount] = useState(0);

  const columns = useMemo(() => ([
    { field: 'orderNo', headerName: 'Order #', minWidth: 140, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'customer', headerName: 'Customer', minWidth: 220, flex: 1, align: 'center', headerAlign: 'center',
      renderCell: (params) => {
        const c = params.row.customer || {};
        const name = c.name || '-';
        const email = c.email || '';
        const phone = c.phone || '';
        return (
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>{name}</div>
            <div style={{ fontSize: 12, color: '#666' }}>{email || phone}</div>
          </div>
        )
      }
    },
    { field: 'total', headerName: 'Amount', minWidth: 120, align: 'center', headerAlign: 'center',
      valueGetter: (p) => p.row.total ?? p.row.subtotal ?? 0 },
    { field: 'status', headerName: 'Status', minWidth: 140, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Chip label={params.value} color={
        params.value === 'Delivered' ? 'success' : params.value === 'Cancelled' ? 'error' : 'warning'
      } size='small'/> },
    { field: 'actions', headerName: 'Actions', minWidth: 200, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction='row' spacing={1}>
          <Button size='small' variant='contained' onClick={() => navigate(`/orders/${params.id}`)}>View</Button>
        </Stack>
      ) }
  ]), [navigate]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const [sortField, sortDir] = sort.split(':');
      const params = {
        page: page + 1,
        limit: pageSize,
        status: status || undefined,
        from: from || undefined,
        to: to || undefined,
        customer: customer || undefined,
        sort: sortDir === 'desc' ? `-${sortField}` : sortField
      };
      const res = await listOrders(params);
      const items = res.data || res.items || res.results || [];
      const total = res.total || res.pagination?.total || items.length;
      setRows(items);
      setRowCount(total);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [sort, page, pageSize, status, from, to, customer]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <>
      <Helmet><title>Orders | Trooper</title></Helmet>
      <Container>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
          <Typography variant='h4'>Orders</Typography>
        </Stack>

        <Stack direction='row' spacing={2} mb={2}>
          <Select size='small' displayEmpty value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value=''>All Status</MenuItem>
            <MenuItem value='Pending'>Pending</MenuItem>
            <MenuItem value='Confirmed'>Confirmed</MenuItem>
            <MenuItem value='Processing'>Processing</MenuItem>
            <MenuItem value='Shipped'>Shipped</MenuItem>
            <MenuItem value='Delivered'>Delivered</MenuItem>
            <MenuItem value='Cancelled'>Cancelled</MenuItem>
          </Select>
          <TextField size='small' type='date' label='From' InputLabelProps={{ shrink: true }} value={from} onChange={e=>setFrom(e.target.value)} />
          <TextField size='small' type='date' label='To' InputLabelProps={{ shrink: true }} value={to} onChange={e=>setTo(e.target.value)} />
          <TextField size='small' label='Customer (id/email/phone)' value={customer} onChange={e => setCustomer(e.target.value)} />
          <Select size='small' value={sort} onChange={e => setSort(e.target.value)}>
            <MenuItem value='createdAt:desc'>Newest</MenuItem>
            <MenuItem value='createdAt:asc'>Oldest</MenuItem>
          </Select>
          <Button variant='outlined' onClick={fetchRows}>Apply</Button>
        </Stack>

        <div style={{ height: '75vh', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r._id}
            pagination
            paginationMode='server'
            pageSizeOptions={[5,10,25,50]}
            rowCount={rowCount}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          {rows.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', color: palette.grey[600], fontSize: '18px', mt: 2 }}>No Row Available</Box>
          )}
        </div>
      </Container>
    </>
  );
}
